import {
  getSupplierTenantSettings,
  updateSupplierTenantSettings,
} from "@/api";
import type { TSupplier } from "@/api/types/supplier";
import type { TTenantSettingsDTO } from "@/api/types/tenant-settings";
import { Refresh, ToggleOn, ToggleOffOutlined } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Labeled, useNotify, useRecordContext } from "react-admin";
import { useAdmin } from "../../../hooks/admin";

export const SupplierSettingsTab = () => {
  const supplier = useRecordContext<TSupplier>();
  const notify = useNotify();
  const { hasPermission } = useAdmin();
  const canUpdate = hasPermission("supplier.update");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scheduledDeliveries, setScheduledDeliveries] = useState(false);
  const [fullSettings, setFullSettings] = useState<TTenantSettingsDTO | null>(null);

  const load = useCallback(async () => {
    if (!supplier?.id) return;
    setLoading(true);
    try {
      const res = await getSupplierTenantSettings(supplier.id);
      setFullSettings(res.settings);
      setScheduledDeliveries(!!res.settings?.orders?.scheduledDeliveries);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "No se pudieron cargar los ajustes del tenant.";
      notify(msg, { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [supplier?.id, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleScheduledDeliveries = async () => {
    if (!supplier?.id || !fullSettings) return;
    setSaving(true);
    const next: TTenantSettingsDTO = {
      ...fullSettings,
      orders: {
        ...fullSettings.orders,
        scheduledDeliveries: !scheduledDeliveries,
      },
    };
    try {
      await updateSupplierTenantSettings(supplier.id, next);
      setScheduledDeliveries(!scheduledDeliveries);
      setFullSettings(next);
      notify("Ajuste actualizado", { type: "success" });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Error al guardar";
      notify(msg, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!supplier?.id) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Cargando configuración…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 720 }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Ajustes del catálogo y pedidos (tenant)
      </Typography>
      <Labeled label="Entregas programadas (subpedidos desde pedido maestro)">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            {scheduledDeliveries ? "Activado" : "Desactivado"}
          </Typography>
          {canUpdate && (
            <IconButton
              color="primary"
              title={scheduledDeliveries ? "Desactivar" : "Activar"}
              onClick={(e) => {
                e.stopPropagation();
                void toggleScheduledDeliveries();
              }}
              disabled={saving}
              size="large"
            >
              {saving ? (
                <Refresh className={saving ? "rotate-icon" : ""} />
              ) : scheduledDeliveries ? (
                <ToggleOn fontSize="large" />
              ) : (
                <ToggleOffOutlined fontSize="large" />
              )}
            </IconButton>
          )}
        </Box>
      </Labeled>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
        Cuando está activo, los clientes del catálogo pueden crear entregas programadas sobre pedidos
        de venta (SELL_OUT) confirmados, sin modificar las líneas del pedido maestro.
      </Typography>
    </Box>
  );
};
